import React from 'react';
import MonitorIcon from '@mui/icons-material/Monitor';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import ApiIcon from '../../components/SvgIcons/ApiIcon';
import MCPIcon from '../../components/SvgIcons/MCPIcon';
import CloudIcon from '../../components/SvgIcons/CloudIcon';

// Asset type constants (the `platform_category` values). This module is the
// single source of truth for asset types — both the raw constants and the
// registry metadata below live here.
export const WEB_APP = 'web_app';
export const API = 'api';
export const MOBILE_APP = 'mobile_app';
export const IT_INFRASTRUCTURE = 'it_infra';
export const CLOUD = 'cloud';
export const MCP = 'mcp';

/** Whether each type is create-enabled in the add-asset UI. */
export const availability = {
  [WEB_APP]: true,
  [API]: true,
  [MOBILE_APP]: true,
  [IT_INFRASTRUCTURE]: true,
  [CLOUD]: false,
  [MCP]: true,
};

/**
 * Single source of truth for asset types (platform_category).
 *
 * Every place that used to hard-code a per-type list, map, or branch should read
 * from this registry so that adding a future asset type is a one-entry change here
 * plus the few genuinely type-shaped JSX blocks that cannot be data-driven.
 *
 * Per-entry fields:
 *  - key          the platform_category value
 *  - title        short display label (mirrors the legacy `platforms` map)
 *  - fullTitle    long display label where one exists (else undefined)
 *  - icon         display icon JSX used by the `platforms` map (className preserved verbatim)
 *  - mappingLabel label used by `platformMapping` (intentionally differs from `title`)
 *  - cssClass     MUI text-field class used on the add-asset row
 *  - availability whether the type is create-enabled in the add-asset UI
 *  - createFields  the distinguishing (non-common) fields the type's create/edit form shows.
 *                  `name`, `department`, and `priority` are common to every type and are NOT listed.
 *  - colors       scoped-inventory legend entry ({ name, color }) or null when the type has none
 *  - cicdEligible whether the type appears in CICD asset-type surfaces
 *  - editRoles    whether user-role editing is enabled for the type (EDIT_ROLES_PLATFORM)
 */
export const ASSET_TYPE_REGISTRY = {
  [WEB_APP]: {
    key: WEB_APP,
    title: 'Web App',
    fullTitle: 'Web Application',
    icon: <MonitorIcon className={'text-primary-500 align-top'} />,
    mappingLabel: 'Web Application',
    cssClass: '',
    availability: availability[WEB_APP],
    createFields: ['target_url', 'num_of_user_roles', 'num_of_dynamic_pages'],
    colors: { name: 'Web App', color: '#12AFB8' },
    cicdEligible: true,
    editRoles: true,
  },
  [API]: {
    key: API,
    title: 'API',
    fullTitle: undefined,
    icon: <ApiIcon />,
    mappingLabel: 'API',
    cssClass: 'secondary',
    availability: availability[API],
    createFields: ['target_url', 'num_of_user_roles', 'num_of_end_points'],
    colors: { name: 'APIs', color: '#228EBC' },
    cicdEligible: true,
    editRoles: true,
  },
  [MOBILE_APP]: {
    key: MOBILE_APP,
    title: 'Mobile App',
    fullTitle: 'Mobile Application',
    icon: <PhoneAndroidIcon className={'text-tertiary-600'} />,
    mappingLabel: 'Mobile App',
    cssClass: 'tertiary',
    availability: availability[MOBILE_APP],
    createFields: ['num_of_user_roles', 'num_of_end_points', 'type_of_os', 'num_of_mobile_screens'],
    colors: { name: 'Mobile App', color: '#FE7754' },
    cicdEligible: false,
    editRoles: true,
  },
  [IT_INFRASTRUCTURE]: {
    key: IT_INFRASTRUCTURE,
    title: 'IT Infrastructure',
    fullTitle: undefined,
    icon: <DeviceHubIcon className={'text-primary-500'} />,
    mappingLabel: 'Infrastructure',
    cssClass: '',
    availability: availability[IT_INFRASTRUCTURE],
    createFields: ['num_of_external_ips', 'num_of_internal_ips'],
    colors: { name: 'Infra', color: '#C745F4' },
    cicdEligible: false,
    editRoles: false,
  },
  [CLOUD]: {
    key: CLOUD,
    title: 'Cloud',
    fullTitle: undefined,
    icon: <CloudIcon />,
    mappingLabel: 'Cloud',
    cssClass: '',
    availability: availability[CLOUD],
    createFields: ['num_of_unique_services'],
    colors: null,
    cicdEligible: false,
    editRoles: false,
  },
  [MCP]: {
    key: MCP,
    title: 'MCP',
    fullTitle: undefined,
    icon: <MCPIcon />,
    mappingLabel: 'MCP',
    cssClass: 'secondary',
    availability: availability[MCP],
    createFields: ['target_url'],
    colors: { name: 'MCP', color: '#228EBC' },
    cicdEligible: true,
    editRoles: true,
  },
};

/** Ordered list of asset-type keys (creation-tab / iteration order). */
export const ASSET_TYPE_KEYS = [WEB_APP, API, MOBILE_APP, IT_INFRASTRUCTURE, CLOUD, MCP];

/** Ordered list of registry entries. */
export const ASSET_TYPES = ASSET_TYPE_KEYS.map((key) => ASSET_TYPE_REGISTRY[key]);

/** Look up a single registry entry (undefined for unknown keys). */
export const getAssetType = (key) => ASSET_TYPE_REGISTRY[key];

/** All asset-type keys. */
export const getAssetTypeKeys = () => ASSET_TYPE_KEYS;

/** `platformMapping`-style label for a type (undefined if unknown). */
export const getAssetTypeLabel = (key) => ASSET_TYPE_REGISTRY[key]?.mappingLabel;

/** MUI text-field class for a type's add-asset row. */
export const getAssetTypeCssClass = (key) => ASSET_TYPE_REGISTRY[key]?.cssClass ?? '';

/** Whether a type's create/edit form shows the given distinguishing field. */
export const assetTypeHasField = (key, field) =>
  Boolean(ASSET_TYPE_REGISTRY[key]?.createFields?.includes(field));

/** Keys of types that have user-role editing enabled (EDIT_ROLES_PLATFORM). */
export const getEditRolePlatforms = () =>
  ASSET_TYPE_KEYS.filter((key) => ASSET_TYPE_REGISTRY[key].editRoles);

/** Keys of types that are CICD-eligible. */
export const getCicdEligiblePlatforms = () =>
  ASSET_TYPE_KEYS.filter((key) => ASSET_TYPE_REGISTRY[key].cicdEligible);
